import { EventEmitter, Injectable } from '@angular/core';
import { FormsService } from './forms.service';
import { Observable, Subject } from 'rxjs';
import { Buffer } from 'buffer';
import { first } from 'rxjs/operators';

import {
    ComponentEntity,
    ComponentType,
    ComponentValidator,
    DataManipulationRule,
    DataObject,
    DataObjectField,
    DataObjectFieldType,
    DataObjectValidator,
    Form,
    ParameterEntity,
    Util,
    ValidatorType
} from '@flux/flux-core';
import { UntypedFormGroup } from '@angular/forms';
import { ConfigService, FluxFormControl, ObjectDataResolverService } from '@flux/flux-dynamic-forms';

@Injectable()
export class CurrentFormService {
    private forms: Form[] = [];

    private preloadedForms: Form[] = [];

    formRequests$: Subject<any>[] = [];

    parameterLoaded$: Subject<any> = new Subject();

    dateComponents = [];

    newDataAvailable: EventEmitter<string> = new EventEmitter<string>();

    formParams = [];

    formParamValues = [];

    private alreadyValidatedFields = [];

    private validationResetTimer = null;

    dataManipulationRules: DataManipulationRule[] = [];

    constructor(
        private formsService: FormsService,
        private configService: ConfigService,
        private objectDataResolverService: ObjectDataResolverService
    ) {}

    init(parentObj: string, action: string, predefinedParams: ParameterEntity[]): any {
        this.dateComponents = [];
        const formKey = parentObj + '_' + action;
        if (formKey in this.formRequests$) {
            if (this.formRequests$[formKey].isStopped) {
                return new Observable(observer => {
                    observer.next(this.forms[formKey]);
                    this.loadAllFormParamsForCurrentForm(this.forms[formKey]);
                    observer.complete();
                });
            } else {
                return this.formRequests$[formKey];
            }
        } else {
            this.formRequests$[formKey] = new Subject();
            this.forms[formKey] = null;
        }
        this.formsService
            .get(parentObj, action)
            .pipe(first())
            .subscribe((form: Form) => {
                this.forms[formKey] = form;
                this.preloadedForms[formKey + '_' + action] = form;
                this.formRequests$[formKey].next(form);
                this.formRequests$[formKey].complete();
                this.loadAllFormParamsForCurrentForm(form);
            });
        return this.formRequests$[formKey];
    }

    initByFormKey(formKey: string): any {
        this.dateComponents = [];
        if (formKey in this.formRequests$) {
            if (this.formRequests$[formKey].isStopped) {
                return new Observable(observer => {
                    observer.next(this.forms[formKey]);
                    this.loadAllFormParamsForCurrentForm(this.forms[formKey]);
                    observer.complete();
                });
            } else {
                return this.formRequests$[formKey];
            }
        } else {
            this.formRequests$[formKey] = new Subject();
            this.forms[formKey] = null;
        }
        this.formsService
            .getByFormKey(formKey)
            .pipe(first())
            .subscribe((form: Form) => {
                this.forms[formKey] = form;
                this.preloadedForms[formKey] = form;
                this.formRequests$[formKey].next(form);
                this.formRequests$[formKey].complete();
                this.loadAllFormParamsForCurrentForm(form);
            });
        return this.formRequests$[formKey];
    }

    resetFormByKey(parentObj): void {
        if (parentObj + '_CREATE' in this.formRequests$) {
            this.formRequests$.splice(this.formRequests$.indexOf(this.formRequests$[parentObj + '_CREATE']), 1);
        }
        if (parentObj + '_UPDATE' in this.formRequests$) {
            this.formRequests$.splice(this.formRequests$.indexOf(this.formRequests$[parentObj + '_UPDATE']), 1);
        }
    }

    resetCurrentFormParams(): void {
        this.formParams = [];
        this.formParamValues = [];
    }

    loadAllFormParamsForCurrentForm(form: Form): void {
        this.configService
            .getAllParamsOfForm(form.fieldsets[0])
            .pipe(first())
            .subscribe((params: ParameterEntity[]) => {
                this.formParams = params;
                this.parameterLoaded$.next(params);
            });
    }

    loadAllDataManipulationRules(form: Form): void {
        this.configService
            .getAllDataManipulationRules(form)
            .pipe(first())
            .subscribe(rules => {
                this.dataManipulationRules = rules;
            });
    }

    getCurrentForm(parentObj: string, action: string): Form {
        const formKey = parentObj + '_' + action;
        return this.forms[formKey];
    }

    public getFormParam(param: string): ParameterEntity {
        return this.formParams.find((x: ParameterEntity) => x.selectorReference === param);
    }

    getFormParamValue(param: string): ParameterEntity {
        return this.formParamValues[param];
    }

    setFormParamValue(param: string, value): ParameterEntity {
        return (this.formParamValues[param] = value);
    }

    markFieldAsValidated(newValidatedField: string): void {
        if (Util.checkIfNull(this.validationResetTimer)) {
            this.validationResetTimer = window.setTimeout(() => {
                this.clearValidatedFields();
            }, 100);
        }
        if (this.isFieldAlreadyValidated(newValidatedField)) {
            return;
        }
        this.alreadyValidatedFields.push(newValidatedField);
    }

    isFieldAlreadyValidated(fieldId: string): boolean {
        return !Util.checkIfNull(
            this.alreadyValidatedFields.find((validatedField: string) => validatedField === fieldId)
        );
    }

    clearValidatedFields(): void {
        window.clearTimeout(this.validationResetTimer);
        this.validationResetTimer = null;
        this.alreadyValidatedFields = [];
    }

    updateDataObjectWithFormValues(controlName: string, formGroup: UntypedFormGroup, dataObject: any): void {
        if (controlName in dataObject || controlName.startsWith('dynamicFields')) {
            if (typeof formGroup.controls[controlName].value === 'object') {
                const dateComponentType = this.getDateComponentType(this.dateComponents[controlName]);
                switch (dateComponentType) {
                    case 'DATE':
                        const dateValue = this.objectDataResolverService.extractDateFromObject(
                            formGroup.controls[controlName].value
                        );
                        this.setNestedValue(controlName, dateValue, dataObject);
                        break;
                    case 'TIME':
                        const timeValue = this.objectDataResolverService.extractTimeFromObject(
                            formGroup.controls[controlName].value
                        );
                        this.setNestedValue(controlName, timeValue, dataObject);
                        break;
                    case 'DATETIME':
                        const dateTimeValue = this.objectDataResolverService.extractDateTimeFromObject(
                            formGroup.controls[controlName].value
                        );
                        this.setNestedValue(controlName, dateTimeValue, dataObject);
                        break;
                    default:
                        const objectValue = this.objectDataResolverService.extractDataFromObject(
                            formGroup.controls[controlName].value
                        );
                        this.setNestedValue(controlName, objectValue, dataObject);
                        break;
                }
            } else {
                const fluxFormControl = formGroup.controls[controlName] as FluxFormControl;
                if (fluxFormControl.controlType === DataObjectFieldType.JSON.toString()) {
                    this.setNestedValue(controlName, JSON.parse(formGroup.controls[controlName].value), dataObject);
                } else if (fluxFormControl.controlType === DataObjectFieldType.JAVASCRIPT.toString()) {
                    this.setNestedValue(
                        controlName,
                        [...Buffer.from(formGroup.controls[controlName].value)],
                        dataObject
                    );
                } else {
                    this.setNestedValue(controlName, formGroup.controls[controlName].value, dataObject);
                }
            }
        }
    }

    setNestedValue(path: string, value: any, dataObject: any): void {
        const segments = path.split('.');
        let current = dataObject;

        // Spezialfall für dynamicFields
        if (segments[0] === 'dynamicFields') {
            // Erstelle eine komplette neue Kopie von dynamicFields
            const newDynamicFields = { ...dataObject.dynamicFields };

            if (segments.length === 2) {
                // Direkter Zugriff auf erste Ebene von dynamicFields
                newDynamicFields[segments[1]] = value;
            } else {
                // Tiefere Verschachtelung in dynamicFields
                let currentLevel = newDynamicFields;
                for (let i = 1; i < segments.length - 1; i++) {
                    const segment = segments[i];
                    currentLevel[segment] = currentLevel[segment] || {};
                    currentLevel = currentLevel[segment];
                }
                currentLevel[segments[segments.length - 1]] = value;
            }

            // Setze die neue Kopie zurück
            dataObject.dynamicFields = newDynamicFields;
            return;
        }

        // Standardfall für alle anderen Properties
        for (let i = 0; i < segments.length - 1; i++) {
            const segment = segments[i];
            if (!(segment in current) || current[segment] === null) {
                current[segment] = {};
            }
            current = current[segment];
        }

        const finalSegment = segments[segments.length - 1];
        current[finalSegment] = value;
    }

    getDateComponentType(element: any): any {
        if (!Util.checkIfNull(element)) {
            return element.type;
        }
        return null;
    }

    getComponentDataByNameAndType(name, componentType: ComponentType, translationKey: string): ComponentEntity {
        const componentEntity: ComponentEntity = new ComponentEntity();
        componentEntity.label = name;
        componentEntity.translationKey = translationKey;
        componentEntity.dataObject = new DataObjectField();
        componentEntity.dataObject.relatedDataObject = new DataObject();
        componentEntity.type = componentType;
        componentEntity.active = true;
        return componentEntity;
    }

    addRequiredValidation(componentEntity: ComponentEntity): ComponentEntity {
        const componentValidator: ComponentValidator = {} as ComponentValidator;
        componentValidator.validatorType = ValidatorType.REQUIRED;
        componentEntity.componentValidator.push(componentValidator);
        const dataObjectFieldValidator: DataObjectValidator = new DataObjectValidator();
        dataObjectFieldValidator.validatorType = ValidatorType.REQUIRED;
        componentEntity.dataObject.dataObjectValidators.push(dataObjectFieldValidator);
        return componentEntity;
    }

    getSelectGroupOptions(componentEntity: ComponentEntity, options: any[]): ComponentEntity {
        options.forEach((option: string, index: number) => {
            const selectOption: ComponentEntity = new ComponentEntity();
            selectOption.type = ComponentType.SELECTOPTION;
            selectOption.label = option;
            selectOption.active = true;
            selectOption.value = option;
            selectOption.divId = index.toString();
            componentEntity.child.push(selectOption);
        });
        return componentEntity;
    }
}
